import { z } from "zod";
import { DocumentPatchOperationSchema } from "./documents.js";
import { EntityLinkCommandSchema } from "./graph.js";
import { DecimalStringSchema, Sha256HexSchema, UuidSchema } from "./primitives.js";
import { ProjectCreateCommandSchema, ProjectUpdateCommandSchema, TaskCreateCommandSchema } from "./work.js";
export const ProposalStateSchema = z.enum([
    "pending",
    "blocked_by_edit_session",
    "applying",
    "accepted",
    "rejected",
    "expired",
    "conflict",
    "failed",
]);
const NoteCreateCommandSchema = z
    .object({
    type: z.literal("note.create"),
    commandId: UuidSchema,
    noteId: UuidSchema,
    workspaceId: UuidSchema,
    parentRevision: DecimalStringSchema,
    title: z.string().max(500).nullable(),
    bodyMarkdown: z.string().max(1_048_576),
})
    .strict();
const NoteUpdateCommandSchema = z
    .object({
    type: z.literal("note.update"),
    commandId: UuidSchema,
    noteId: UuidSchema,
    revision: DecimalStringSchema,
    title: z.string().max(500).nullable().optional(),
    bodyMarkdown: z.string().max(1_048_576).optional(),
    tags: z.array(z.string().min(1).max(100)).max(100).optional(),
})
    .strict();
const DocumentPatchCommandSchema = z
    .object({
    type: z.literal("document.patch"),
    commandId: UuidSchema,
    documentId: UuidSchema,
    baseVersionId: UuidSchema,
    baseSha256: Sha256HexSchema,
    operations: z.array(DocumentPatchOperationSchema).min(1).max(1_000),
})
    .strict();
const DocumentReplacementCommandSchema = z
    .object({
    type: z.enum(["document.replace_from_git", "document.replace_recovered"]),
    commandId: UuidSchema,
    documentId: UuidSchema,
    provenanceBaseVersionId: UuidSchema,
    expectedCurrentVersionId: UuidSchema,
    expectedCurrentSha256: Sha256HexSchema,
    candidateSha256: Sha256HexSchema,
    quarantineReceiptId: UuidSchema,
})
    .strict();
export const ProposalCommandSchema = z.union([
    NoteCreateCommandSchema,
    NoteUpdateCommandSchema,
    z
        .object({
        type: z.literal("note.archive"),
        commandId: UuidSchema,
        noteId: UuidSchema,
        revision: DecimalStringSchema,
        archived: z.boolean(),
    })
        .strict(),
    ProjectCreateCommandSchema.extend({ type: z.literal("project.create") }).strict(),
    ProjectUpdateCommandSchema.and(z.object({ type: z.literal("project.update") }).strict()),
    TaskCreateCommandSchema.extend({ type: z.literal("task.create") }).strict(),
    EntityLinkCommandSchema.and(z.object({ type: z.enum(["entity_link.create", "entity_link.remove"]) }).strict()),
    DocumentPatchCommandSchema,
    DocumentReplacementCommandSchema,
]);
export const ProposalOriginSchema = z.discriminatedUnion("kind", [
    z
        .object({
        kind: z.literal("model_attempt"),
        attemptId: UuidSchema,
        leaseGeneration: DecimalStringSchema,
        manifestDigest: Sha256HexSchema,
        deviceSignature: z.string().min(1).max(512),
    })
        .strict(),
    z
        .object({
        kind: z.literal("human_staging"),
        recentAuthSessionId: UuidSchema,
        quarantineReceiptId: UuidSchema,
        inputSha256: Sha256HexSchema,
    })
        .strict(),
    z
        .object({
        kind: z.literal("local_mcp"),
        mcpRequestId: UuidSchema,
        operationId: UuidSchema,
        deviceGrantEpoch: DecimalStringSchema,
        signedBaseProof: z.string().min(1).max(2_048),
    })
        .strict(),
]);
export const ProposalStageSchema = z
    .object({
    proposalId: UuidSchema,
    groupId: UuidSchema.nullable(),
    workspaceId: UuidSchema,
    reason: z.string().min(1).max(4_096),
    command: ProposalCommandSchema,
    origin: ProposalOriginSchema,
})
    .strict();
export const ProposalDecisionSchema = z
    .object({
    commandId: UuidSchema,
    proposalId: UuidSchema,
    action: z.enum(["accept", "reject"]),
    proposalRevision: DecimalStringSchema,
})
    .strict();
export const ProposalProjectionSchema = z
    .object({
    id: UuidSchema,
    groupId: UuidSchema.nullable(),
    workspaceId: UuidSchema,
    state: ProposalStateSchema,
    reason: z.string().min(1).max(4_096),
    command: ProposalCommandSchema,
    origin: ProposalOriginSchema,
    revision: DecimalStringSchema,
    createdByAccountId: UuidSchema,
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
})
    .strict();
export const ProposalListSchema = z.object({ items: z.array(ProposalProjectionSchema) }).strict();
export const ProposalPreviewSchema = z
    .object({
    proposalId: UuidSchema,
    commandDigest: Sha256HexSchema,
    base: ProposalCommandSchema,
    candidateId: UuidSchema.nullable(),
})
    .strict();
//# sourceMappingURL=proposals.js.map